import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk/';
import {
  AnalyticsService,
  BreadcrumbItem,
  ModalService,
  ResponsiveService,
  SimpleModalConfig,
} from '@frk/eds-components';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { LiteratureRulesHelperService } from '@literature/services/literature-rules-helper.service';
import { getFirstActiveShareClass } from '@literature/literature-utils';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { TranslateService } from '@shared/translate/translate.service';
import {
  CartActionPerformed,
  InfoItem,
  ItemDetail,
  LiteratureContentDocument,
  LiteratureDocument,
  LiteratureDocumentListingData,
  LiteratureDocumentListingState,
  ModalId,
  FundId,
  FundIdentifier,
  ShareClassCode,
} from '@types';
import { MONTH_YEAR_DATE_FORMAT } from '@utils/app.constants';
import { Logger } from '@utils/logger';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ItemDetailService } from './services/item-detail.service';
import { SiteConfigService } from '@services/site-config.service';
import { SegmentService } from '@services/segment.service';
import { RoleSelectorService } from '@services/role-selector.service';
import { DatadogService } from '@services/datadog.service';

const logger = Logger.getLogger('ItemDetailComponent');

@Component({
  selector: 'ft-item-detail',
  templateUrl: './item-detail.component.html',
  styleUrls: ['./item-detail.component.scss'],
  providers: [ItemDetailService],
})
export class ItemDetailComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  public isHandheld$: Observable<boolean>;
  public isMobile$: Observable<boolean>;
  private cartSubscription: Subscription;
  private literatureServiceSubscription: Subscription;
  private cartDeleteSubscription: Subscription;
  public literatureContentDocument: LiteratureContentDocument;
  private addAccountLink: string;
  private errorCode: number;
  public dataLoaded = false;
  public data: ItemDetail;
  private paramMap: Map<string, string> = new Map();
  public infoData: InfoItem[] = [];
  public isOnlineAccount: boolean;
  @Input() component!: BrComponent;
  @Input() page!: Page;
  public addedInCart: boolean;
  public dialogData: LiteratureDocument[] = [];
  public detailBreadcrumbs: BreadcrumbItem[];
  private newDateFormat: string;
  public isCartConfigured = true;
  public isPrintOnly = false;
  private segmentSubscription: Subscription;

  constructor(
    private itemDetailService: ItemDetailService,
    private responsiveService: ResponsiveService,
    private translate: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
    private cartService: CartHandlerService,
    private titleService: Title,
    private metaService: Meta,
    private analytics: AnalyticsService,
    public dateFormatter: DatePipe,
    private segmentService: SegmentService,
    private roleSelectorService: RoleSelectorService,
    private analyticsService: AnalyticsService,
    private datadogService: DatadogService,
    private modalService?: ModalService,
    private literatureRules?: LiteratureRulesHelperService,
    private literatureConfigService?: LiteratureGlobalConfigService,
    private siteConfigService?: SiteConfigService
  ) {
    super();
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.isMobile$ = this.responsiveService.isMobile$();
  }

  ngOnInit() {
    this.setBreadcrumbItems();
    const content = this.document?.getData();
    const config = this.component?.getModels()?.config;
    this.addAccountLink = content?.moreLink;
    if (content) {
      this.literatureContentDocument = this.itemDetailService.isLiteratureAddOnAccount(
        encodeURIComponent(config?.literatureCode),
        content.literatureListContent
      );
    }
    this.detailBreadcrumbs = this.breadcrumbItems;
    this.paramMap.set(
      'literatureCode',
      encodeURIComponent(config?.literatureCode)
    );
    const columnDislayConfig = this.literatureConfigService?.literatureConfig
      .fundDocumentGridColumn;
    this.isCartConfigured = columnDislayConfig?.indexOf('ADD_TO_CART') !== -1;
    this.cartSubscription = this.cartService
      .getCartItems()
      .subscribe((item: LiteratureDocumentListingState) => {
        if (
          CartActionPerformed.ADDED_FROM_LITERATURE_DETAIL === item.cartAction
        ) {
          this.dialogData = [];
          this.dialogData = item.cartItems;
          this.openModal(ModalId.Cart_Modal);
          this.changeDetectorRef.detectChanges();
        }
      });
    this.cartDeleteSubscription = this.cartService
      .getDeletedDocId()
      ?.subscribe((docIds: string[]) => {
        if (docIds?.includes(config.literatureCode)) {
          this.addedInCart = false;
          this.changeDetectorRef.detectChanges();
        }
      });
    this.literatureServiceSubscription = this.itemDetailService
      .populate(this.paramMap)
      .pipe(
        finalize(() => {
          this.dataLoaded = true;
          // as per NGC-16203, we will always display meta tag with 404 status code for inactive/not available funds
          this.metaService.updateTag({
            name: 'prerender-status-code',
            content: '404',
          });
        })
      )
      .subscribe(
        (litData: ItemDetail) => {
          this.literatureRules.checkLiteratureRules(litData);
          this.data = litData;
          this.addedInCart = this.cartService.checkCartStatus(this.data.docId);
          this.infoData = this.prepareViewData(this.data);
          this.titleService.setTitle(litData?.fundName);
          this.metaService.updateTag({
            name: 'description',
            content: litData?.dctermsDescription,
          });
          this.metaService.updateTag({
            name: 'og:title',
            content: litData?.fundName,
          });
          this.metaService.updateTag({
            name: 'og:description',
            content: litData?.dctermsDescription,
          });
          if (litData?.imageUrl) {
            this.metaService.updateTag({
              property: 'og:image',
              content: litData?.imageUrl,
            });
          }

          if (this.showBreadcrumb && this.detailBreadcrumbs) {
            this.detailBreadcrumbs[this.detailBreadcrumbs.length - 1] = {
              content: this.data.fundName,
            };
          }
          this.isDownloadAvailable();
          this.changeDetectorRef.detectChanges();
        },
        (errorResponse) => {
          if (errorResponse?.status === 404) {
            logger.warn(
              '[populate]' +
                errorResponse?.message +
                ' code: ' +
                errorResponse?.error?.message
            );
          }
          this.errorCode = errorResponse?.error?.code;
          this.changeDetectorRef.detectChanges();
        }
      );
    this.isOnlineAccount = this.itemDetailService.getConfig(
      config.literatureCode
    );
  }

  /**
   *
   * @param data ItemDetail object
   * @param downloadType which download type - finra or pdf
   * @param event- to stop default behaviour of anchor tag
   * create link and call service to open url
   * while downloading the pdf - same doc is also checked out to place cart order(if eligible)
   */
  goToLink(data: ItemDetail, downloadType: string, event: MouseEvent) {
    event.preventDefault();
    let link: string =
      'finra' === downloadType ? data.finraLink : data.pdfDownloadLink;
    const isDocumentsWithPreview: boolean = this.literatureConfigService.requiresPreview(
      data.dctermsType
    );

    // Reverting WDE-2498: Literature - US Bypass links not working
    if (isDocumentsWithPreview) {
      // WDE-2860 check if first fundId/shareclass is on active list
      // if not, take next shareclass
      // if none shareclass is active, take next fundid
      // if none is active, take the first one and log error

      const activeShareClasses = this.siteConfigService.product.general
        .activeList;

      let activeFund: FundId;
      let activeShareClass: ShareClassCode;

      [activeFund, activeShareClass] = getFirstActiveShareClass(
        data.frkOneTISNumber,
        data.frkShareClassCode,
        activeShareClasses
      );

      // we assign default value in case there is no active shareclass - error will be triggered from lit-preview
      if (!activeFund || !activeShareClass) {
        activeFund = data.frkOneTISNumber[0] as FundId;
        activeShareClass = data.frkShareClassCode[0] as ShareClassCode;
      }

      if (data.frkOneTISNumber?.length === 1) {
        const litDownloadUrlChk = this.siteConfigService.getFundLink(
          activeFund,
          activeShareClass,
          data.fundName,
          '' as FundIdentifier, // TODO add here ticker symbol after available in litAPI
          true
        );

        if (litDownloadUrlChk) {
          link = litDownloadUrlChk + '#' + data?.dctermsType?.toLowerCase();
        }
      }
    }

    // check if segment or user_role is set in LS or cookie
    this.segmentSubscription = this.segmentService
      .isSegmentSet$()
      .subscribe((isSegmentSet) => {
        // open role selctor model if segment or user_role is not set
        if (!isSegmentSet) {
          this.showRoleSelectorDialog();
        }
        // if segment is set in LS or cookie open download link in new tab
        else if (isSegmentSet) {
          const trackingObj = {
            litCode: data.docId,
            litPreviewEnabled: isDocumentsWithPreview,
            user_role: this.segmentService.getCurrentSegmentId(),
          };
          logger.debug('literature details download', trackingObj);
          this.datadogService.addAction(
            'literature details download',
            trackingObj
          );

          this.itemDetailService.goToLink(link);
        }
      });

    this.analytics.trackEvent({
      location: '',
      event: 'file_download',
      file_name: data.docId,
      file_extension: data.fileExtension,
      link_url: link,
      embed_id: data?.externalId || '',
      document_title: data?.dctermsTitle || '',
      document_source: data?.documentSourceSystem || '',
    });
    this.cartService.checkoutOrder([{ literatureCode: data.docId, qty: 1 }]);
  }

  /**
   * opens role selector modal
   */
  public showRoleSelectorDialog(): void {
    this.analyticsService.trackEvent({
      event: 'lightbox_display',
      action: 'role_select',
      label: '',
    });
    this.analyticsService.trackEvent({
      event: 'utility_navigation',
      link_url: '',
      link_text: '',
    });

    this.roleSelectorService.openRoleSelectorModal();
  }

  /**
   *
   * @param data ItemDetail object
   * @param event - to stop default behaviour of anchor tag
   * add clicked document to cart service as add to cart functionality
   * tracks some analytics event also on meta data of document
   */
  addToCartHandler(data: ItemDetail, event: MouseEvent) {
    event.stopPropagation();
    this.addedInCart = true;
    const cartData: LiteratureDocumentListingData = { literatureData: data };
    this.cartService.addToCart(
      [cartData],
      CartActionPerformed.ADDED_FROM_LITERATURE_DETAIL
    );
    this.analytics.trackEvent({
      event: 'cart_engagement',
      action: 'add to cart',
      file_name: data?.dctermsTitle,
      file_lit_code: data?.docId,
    });
  }

  /**
   *
   * @param data literture object
   * @param event - for future use
   * open account page((Account registration) by using link provided in data
   */
  openAnAccountHandler(data: ItemDetail, event: MouseEvent) {
    if (this.addAccountLink) {
      this.itemDetailService.goToLink(this.addAccountLink);
    }
  }

  /**
   *
   * @param data ItemDetail object
   * This function creates the data mapping for mobile/handheld device
   * @returns infoItem
   */
  prepareViewData(data: ItemDetail): InfoItem[] {
    if (data.publicationDate) {
      this.newDateFormat = this.dateFormatter.transform(
        data.publicationDate,
        MONTH_YEAR_DATE_FORMAT
      );
    }
    data.publicationDate = `${this.translate.instant(
      'literature.publication-date'
    )}: ${this.newDateFormat ? this.newDateFormat : `-`}`;
    if (!data.isInvestor) {
      this.infoData.push({
        label: this.translate.instant('literature.client-use'),
        value: this.translate.instant('literature.' + data.clientUse),
        labelSmall: true,
        link: this.translate.instant('literature.finra-letter'),
      });
    }
    this.infoData.push(
      {
        label: this.translate.instant('literature.lit-code'),
        value: data.docId,
      },
      {
        label: this.translate.instant('literature.next-update'),
        value: data.nextUpdateDate,
      },
      {
        label: data.fileExtension
          ? this.translate.instant('literature.format')
          : '',
        value: `${
          data.fileExtension ? data.fileExtension.toLocaleUpperCase() : ''
        }${data.fileSize ? '(' + data.fileSize + ')' : ''}`,
      }
    );
    return this.infoData;
  }

  /**
   *
   * @param modelId - cart modal
   * open modal when user click to add to cart button
   * calls eds modal service
   */
  openModal(modelId: string) {
    const config: SimpleModalConfig = {
      id: modelId,
      type: 'simple',
      body: '',
      themeTitle: 'dark',
      eventName: 'lightbox_display',
    };
    this.modalService.open(modelId, config);
  }
  closeDialog(modelId: string) {
    this.modalService.close(modelId);
  }

  /**
   *
   * @param docId - doc need to deleted from add to cart modal
   * once user open the modal - same doc can be deleted also from modal
   * docId need to be deleted from local storage
   * call is getting deligated to cartHandler service to remove document from cart
   */
  updateStorage(docId: string) {
    this.cartService.removeFromCart([docId]);
  }

  isDownloadAvailable() {
    // covers scenarios for printOnly as 'Y', 'N' and undefined
    this.isPrintOnly =
      this.data.printOnly === 'Y' && this.data?.downloadUrl ? true : false;
  }

  ngOnDestroy(): void {
    this.literatureServiceSubscription?.unsubscribe();
    this.cartSubscription?.unsubscribe();
    this.cartDeleteSubscription?.unsubscribe();
    this.segmentSubscription?.unsubscribe();
  }
}
